import { useState, useEffect, useMemo } from "react";
import { useLocalization } from "@fluent/react";
import { Button, Row } from "@narmi/design_system";
import { Dialog, catcat as cc } from "cerulean";
import { cents } from "byzantine";
import filters from "byzantine/src/filters";
import { type ReversalReason } from "byzantine/src/dbbl/businessLogic/modules/achPayments";
import { type FormattedBulkPayment } from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import {
  ReversalReasonSelect,
  ReversalDetailRow,
  ReversalReceipt,
  ReversalReceiptFooter,
  useReversalReceiptTitle,
  type ReversalReceiptRow,
} from "../common";
import styles from "../common/reversalShared.module.scss";
import { ReviewPaymentDialog } from "../CreateBulk";

export type BulkReversePaymentModalProps = {
  isOpen: boolean;
  showReceipt: boolean;
  bulkPayment: FormattedBulkPayment | null;
  // the eligible / selected payments being reversed (also feeds "view payments")
  payments: API.FormattedPaymentActivity[];
  handleClose: () => void;
  handleReverse: (reason: ReversalReason) => void;
  submitting?: boolean;
  // disable the "reverse payment" button (e.g. nothing to reverse)
  disableSubmit?: boolean;
  reversalDualApprovalId?: API.DualApprovalId | null;
};

const BulkReversePaymentModal = ({
  isOpen,
  showReceipt,
  bulkPayment,
  payments,
  handleClose,
  handleReverse,
  submitting = false,
  disableSubmit = false,
  reversalDualApprovalId,
}: BulkReversePaymentModalProps) => {
  const { l10n } = useLocalization();
  const [reason, setReason] = useState<ReversalReason | "">("");
  const [displayError, setDisplayError] = useState(false);
  const [showPaymentsDialog, setShowPaymentsDialog] = useState(false);
  const [paymentsPageNumber, setPaymentsPageNumber] = useState(1);
  const receiptTitle = useReversalReceiptTitle();

  useEffect(() => {
    if (!isOpen) {
      setReason("");
      setDisplayError(false);
      setShowPaymentsDialog(false);
      setPaymentsPageNumber(1);
    }
  }, [isOpen]);

  // sending / collecting / net totals reflect the payments being reversed (the
  // selected subset), not the whole bulk
  const {
    creditTotalDollars,
    debitTotalDollars,
    netTotalDollarsWithSign,
    paymentCreditCount,
    paymentDebitCount,
  } = useMemo(() => {
    let sendCents = 0;
    let collectCents = 0;
    let sendCount = 0;
    let collectCount = 0;
    payments.forEach((p) => {
      if (p.isCollectPayment) {
        collectCents += p.amount;
        collectCount += 1;
      } else {
        sendCents += p.amount;
        sendCount += 1;
      }
    });
    // net total: collect (debit) - send (credit); positive gets a "+" sign
    const netCents = collectCents - sendCents;
    const netDollars = filters.currency(cents(netCents), { hasDecimal: false });
    return {
      creditTotalDollars: filters.currency(cents(sendCents), {
        hasDecimal: false,
      }),
      debitTotalDollars: filters.currency(cents(collectCents), {
        hasDecimal: false,
      }),
      netTotalDollarsWithSign: netCents > 0 ? `+${netDollars}` : netDollars,
      paymentCreditCount: sendCount,
      paymentDebitCount: collectCount,
    };
  }, [payments]);

  if (!bulkPayment) return null;

  const {
    institutionAccount,
    secCode,
    companyName,
    companyId,
    entryDesc,
    groupPosting,
  } = bulkPayment;

  const handleSubmit = () => {
    if (!reason) {
      setDisplayError(true);
      return;
    }
    handleReverse(reason);
  };

  const handleReasonChange = (value: ReversalReason | "") => {
    setReason(value);
    if (displayError) {
      setDisplayError(false);
    }
  };

  const handleCopyPaymentLink = () => {
    if (!reversalDualApprovalId) return;
    const approvalUrl = `${window.location.origin}/approvals/pending?dualApprovalId=${reversalDualApprovalId}`;
    navigator.clipboard.writeText(approvalUrl);
  };

  const receiptRows: ReversalReceiptRow[] = [
    {
      label: l10n.getString("label-sending"),
      value: paymentCreditCount > 0 ? creditTotalDollars : null,
    },
    {
      label: l10n.getString("label-collecting"),
      value: paymentDebitCount > 0 ? debitTotalDollars : null,
    },
    {
      label: l10n.getString("label-net-total"),
      value: netTotalDollarsWithSign,
    },
  ];

  const dialogTitle = showReceipt
    ? receiptTitle
    : l10n.getString("label-reverse-payment");

  const renderReversalForm = () => (
    <div>
      <p className="fontColor--secondary margin--top--l">
        {l10n.getString("reverse-payment-modal-subtitle")}
      </p>

      <div className={styles.modalSection}>
        <ReversalReasonSelect
          reason={reason}
          onChange={handleReasonChange}
          errorText={
            displayError
              ? l10n.getString("reverse-payment-reason-required")
              : undefined
          }
        />
      </div>

      <div className={cc(styles.modalSection, "margin--top--l")}>
        <p className="fontColor--heading fontWeight--normal fontSize--l margin--bottom--m">
          {l10n.getString("single-ach-payment-review-and-confirm-title")}
        </p>

        {paymentCreditCount > 0 && (
          <ReversalDetailRow
            label={l10n.getString("label-sending")}
            value={`${creditTotalDollars} ${l10n.getString("via-payments-cta", {
              total: paymentCreditCount,
            })}`}
          />
        )}
        {paymentDebitCount > 0 && (
          <ReversalDetailRow
            label={l10n.getString("label-collecting")}
            value={`${debitTotalDollars} ${l10n.getString("via-payments-cta", {
              total: paymentDebitCount,
            })}`}
          />
        )}
        <ReversalDetailRow
          label={l10n.getString("label-net-total")}
          value={netTotalDollarsWithSign}
        />

        <Row alignItems="center" className="margin--bottom--m">
          <Row.Item>
            <span className="fontColor--secondary">
              {l10n.getString("label-bulk-details")}
            </span>
          </Row.Item>
          <Row.Item shrink>
            <Button kind="plain" onClick={() => setShowPaymentsDialog(true)}>
              {l10n.getString("view-payments-cta", { total: payments.length })}
              <span className="narmi-icon-chevron-right margin--left--xs" />
            </Button>
          </Row.Item>
        </Row>

        {groupPosting && (
          <p className="fontColor--secondary fontSize--s margin--top--xs">
            {l10n.getString("group-posting-copy")}
          </p>
        )}
      </div>

      <div className={cc(styles.modalSection, "margin--top--l")}>
        <h6 className="fontColor--heading fontWeight--bold fontSize--xs margin--bottom--s">
          {l10n.getString("ach-payment-payment-details-title").toUpperCase()}
        </h6>
        <ReversalDetailRow
          label={l10n.getString("label-account")}
          value={
            institutionAccount
              ? `${
                  institutionAccount.name
                }-${institutionAccount.getMaskedNumber()}`
              : undefined
          }
        />
        <ReversalDetailRow
          label={l10n.getString("label-transaction-type")}
          value={
            secCode
              ? l10n.getString("label-std-code-payment", { code: secCode })
              : undefined
          }
        />
        <ReversalDetailRow
          label={l10n.getString("label-company-name")}
          value={companyName}
        />
        <ReversalDetailRow
          label={l10n.getString("label-company-id")}
          value={companyId}
        />
        <ReversalDetailRow
          label={l10n.getString("ach-payment-entry-desc")}
          value={entryDesc}
        />
      </div>
    </div>
  );

  const renderFooter = () => (
    <Row alignItems="center" justifyContent="end">
      <Row.Item shrink>
        <Button kind="secondary" onClick={handleClose}>
          {l10n.getString("button-back")}
        </Button>
      </Row.Item>
      <Row.Item shrink>
        <Button onClick={handleSubmit} disabled={submitting || disableSubmit}>
          {l10n.getString("label-reverse-payment")}
        </Button>
      </Row.Item>
    </Row>
  );

  return (
    <>
      <Dialog
        width="595px"
        isOpen={isOpen && !showPaymentsDialog}
        onUserDismiss={handleClose}
        title={dialogTitle}
        footer={
          showReceipt ? (
            <ReversalReceiptFooter
              handleClose={handleClose}
              handleCopyPaymentLink={handleCopyPaymentLink}
            />
          ) : (
            renderFooter()
          )
        }
      >
        {showReceipt ? (
          <div className={styles.modalSection}>
            <ReversalReceipt rows={receiptRows} />
          </div>
        ) : (
          renderReversalForm()
        )}
      </Dialog>

      <ReviewPaymentDialog
        isOpen={showPaymentsDialog}
        handleClose={() => setShowPaymentsDialog(false)}
        payments={payments}
        pageNumber={paymentsPageNumber}
        setPageNumber={setPaymentsPageNumber}
        paymentsShape="paymentActivity"
      />
    </>
  );
};

export default BulkReversePaymentModal;
